// hooks/formatter .js

// import { ref, watch, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import _, { cloneDeep } from 'lodash-es'
import { reactive, toRefs, toRef, watch } from 'vue'
import { isset, isString, isObject } from '@/utils/helpers'

// by convention, composable function names start with "use"
export default function useValidation (props) {
  // state encapsulated and managed by the composable
  // const { d } = useI18n({ useScope: 'global' })

  // const formatterColumns = ref(headers.filter((h) =>
    //   h.hasOwnProperty('formatter') && h.formatter.length > 0
  // ))

  const { valid } = toRefs(props)
  const { t } = useI18n()

  const state = reactive({
    validModel: valid?.value ?? null,
  })

  const ruleMethods = reactive({
    minRule: (l, msg) => v => (!!v && (typeof v === 'string' ? v.trim().length : v.length) >= l) || msg || `${t('validation.min_label')} ${l} ${Array.isArray(v) ? t('validation.selections') : t('validation.characters')}`,
    maxRule: (l, msg) => v => (!v || v.length <= l) || msg || `${t('validation.max_label')} ${l} ${Array.isArray(v) ? t('validation.selections') : t('validation.characters')}`,
    nameRule: (msg) => v => {
      if (!v) return true;
      const trimmed = v.trim().replace(/\s+/g, ' ');
      return /^[\p{L}'\-]+(?: [\p{L}'\-]+)*$/u.test(trimmed) || msg || t('validation.only_letters_apostrophes_hyphens');
    },
    // requiredRule: msg => v => !!v || msg || 'Required',
    emailRule: (options = {}, msg) => v => {
        if (!v) return true;

        const {
            allowedDomains = [], // Specific allowed domains
            blockedDomains = [], // Specific blocked domains
            allowSubdomains = true, // Allow email addresses with subdomains
            minLength = 1, // Minimum length before @
            maxLength = 254, // Maximum total length (RFC 5321)
            strict = false // Stricter validation
        } = options;

        // Basic format check
        const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!basicEmailRegex.test(v)) {
            return msg || t('validation.email');
        }

        // Strict format check
        if (strict) {
            const strictEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
            if (!strictEmailRegex.test(v)) {
                return msg || t('validation.email_contains_invalid_characters');
            }
        }

        // Length checks
        const [localPart, domain] = v.split('@');
        if (localPart.length < minLength) {
            return msg || t('validation.email_username_min_length', { minLength });
        }
        if (v.length > maxLength) {
            return msg || t('validation.email_max_length', { maxLength });
        }

        // Domain checks
        if (allowedDomains.length > 0) {
            const emailDomain = domain.toLowerCase();
            const isAllowed = allowedDomains.some(allowed => {
                if (allowSubdomains) {
                    return emailDomain.endsWith(allowed.toLowerCase());
                }
                return emailDomain === allowed.toLowerCase();
            });
            if (!isAllowed) {
                return msg || t('validation.email_domain_must_be_one_of', { allowedDomains: allowedDomains.join(', ') });
            }
        }

        // Blocked domains check
        if (blockedDomains.length > 0) {
            const emailDomain = domain.toLowerCase();
            const isBlocked = blockedDomains.some(blocked => {
                if (allowSubdomains) {
                    return emailDomain.endsWith(blocked.toLowerCase());
                }
                return emailDomain === blocked.toLowerCase();
            });
            if (isBlocked) {
                return msg || t('validation.email_domain_not_allowed');
            }
        }

        // Check for common typos in popular domains
        const commonTypos = {
            'gmial': 'gmail',
            'gmal': 'gmail',
            'gamil': 'gmail',
            'yaho': 'yahoo',
            'ymail': 'yahoo',
            'hotmal': 'hotmail',
            'hotnail': 'hotmail',
            'outloo': 'outlook'
        };

        const domainWithoutTLD = domain.split('.')[0].toLowerCase();
        if (commonTypos[domainWithoutTLD]) {
            return msg || t('validation.email_typo_suggestion', { suggestion: commonTypos[domainWithoutTLD] });
        }

        return true;
    },
    requiredRule: (type ='classic',  minOrExact = 1, max, msg) => v => {
      switch(type) {
        case 'classic':
          return !!v || msg || t('validation.required_simple');
        case 'array':
        case 'object':
          max = _.toNumber(max)
          max = _.isNaN(max) ? -1 : max;
          let $msg = ((minOrExact == max || max < 0)
            ? t('Requires exactly {numberOfItems} items', { numberOfItems: minOrExact })
            : t('Requires at least {numberOfItems} items', { numberOfItems: minOrExact }) + ((max != Infinity  && max != undefined) ? ', and maximum of: {max}' : ''));
            // : t('Requires at least {numberOfItems}${((max != Infinity  && max != undefined) ? \', and maximum of:\' + max : \'\')}) elements', { numberOfItems: minOrExact, max: max }));
          // let $msg = ((max != Infinity) ? ', maximum:' + max : '');
          if(Array.isArray(v)) {
            return v.length >= minOrExact && ( max < 0 || v.length <= max) || msg || $msg;
          }
          else if(isObject(v)) {
            return  Object.keys(v).length >= minOrExact &&  (max < 0 || Object.keys(v).length <= max) || msg || $msg;
          }

          if(v == null) {
            return msg || t('validation.must_select_at_least_one_item');
          }

          return 'dev error: nsupported value type';
        default:
          return 'dev error: unknown rule type';
      }
    },
    arrayRule: (msg) => v => Array.isArray(v) || msg || t('validation.value_must_be_array'),
    // requiredArrayRule: (msg, l = 1) => v => (Array.isArray(v) && v.length >= l) || msg || ''
    // confirmedRule: (confirmInputValue, msg) => v => {
    //   // const _val = toRef('model.' + confirmationValue)
    //   return v === confirmInputValue || msg || 'Passwords do not match'
    //   // return v === this.model[confirmationValue] || msg || 'Passwords do not match'
    // }

    // Numeric validation rules
    numberRule: (msg) => v => !isNaN(parseFloat(v)) && isFinite(v) || msg || t('validation.must_be_valid_number'),
    integerRule: (msg) => v => Number.isInteger(Number(v)) || msg || t('validation.must_be_integer'),
    minValueRule: (min, msg) => v => v === undefined || v === null || Number(v) >= min  || msg || t('validation.must_be_at_least', { min }),
    maxValueRule: (max, msg) => v => !v || Number(v) <= max || msg || t('validation.must_not_exceed', { max }),

    // String format validation rules
    phoneRule: (msg) => v => !v || /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(v) || msg || t('validation.invalid_phone_number'),
    urlRule: (msg) => v => !v || /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(v) || msg || t('validation.invalid_url'),
    alphaRule: (msg) => v => !v || /^[A-Za-z]+$/.test(v) || msg || t('validation.only_letters_allowed'),
    alphaNumRule: (msg) => v => !v || /^[A-Za-z0-9]+$/.test(v) || msg || t('validation.only_letters_numbers_allowed'),

    // Password validation rules
    passwordRule: (minLength = 8, msg) => v => !v || /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{${minLength},}$/.test(v) ||
        msg || t('validation.password_min_length', { minLength }),
    strongPasswordRule: (minLength = 8, msg) => v => !v || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{${minLength},}$/.test(v) ||
        msg || t('validation.password_strong', { minLength }),

    // Date validation rules
    dateRule: (msg) => v => !v || !isNaN(Date.parse(v)) || msg || t('validation.invalid_date_format'),
    futureDateRule: (interval = 0, unit = 'day', msg) => v => {
        if (!v) return true;
        let today = new Date();
        today.setHours(0, 0, 0, 0);

        const futureDate = new Date(today);

        if(_.isString(interval)) {
          interval = parseInt(interval)
        }

        if(['days', 'minutes', 'hours', 'months', 'years'].includes(unit)) {
          unit = unit.replace('s', '')
        }

        switch (unit.toLowerCase()) {
            case 'minute':
                futureDate.setMinutes(today.getMinutes() + interval);
                break;
            case 'hour':
                futureDate.setHours(today.getHours() + interval);
                break;
            case 'day':
                futureDate.setDate(today.getDate() + interval);
                break;
            case 'month':
                futureDate.setMonth(today.getMonth() + interval);
                break;
            case 'year':
                futureDate.setFullYear(today.getFullYear() + interval);
                break;
            default:
                futureDate.setDate(today.getDate() + interval);
        }

        let humanIntervalLabel = null;
        if (interval === 0) {
          try {
            const mValue = window.$moment(new Date(v));
            const mFuture = window.$moment(futureDate);
            switch ((unit || '').toLowerCase()) {
              case 'minute':
                humanIntervalLabel = t('fields.this_minute');
                break;
              case 'hour':
                humanIntervalLabel = t('fields.this_hour');
                break;
              case 'month':
                humanIntervalLabel = t('fields.this_month');
                break;
              case 'year':
                humanIntervalLabel = t('fields.this_year');
                break;
              default:
                humanIntervalLabel = t('fields.today');
            }
          } catch (e) {}
        }

        return new Date(v) >= futureDate || msg
          || (interval === 0
            ? t('validation.date_must_be_now', { interval: humanIntervalLabel || t('fields.' + unit, interval) })
            : (interval > 0
              ? t('validation.date_must_be_future', { interval: t('fields.' + unit, interval) })
              : t('validation.date_must_be_past', { interval: t('fields.' + unit, interval) })
            )
          );
    },
    pastDateRule: (interval = 0, unit = 'days', msg) => v => {
        if (!v) return true;
        const today = new Date();
        const pastDate = new Date(today);

        switch (unit.toLowerCase()) {
            case 'minutes':
                pastDate.setMinutes(today.getMinutes() - interval);
                break;
            case 'hours':
                pastDate.setHours(today.getHours() - interval);
                break;
            case 'days':
                pastDate.setDate(today.getDate() - interval);
                break;
            case 'months':
                pastDate.setMonth(today.getMonth() - interval);
                break;
            case 'years':
                pastDate.setFullYear(today.getFullYear() - interval);
                break;
            default:
                pastDate.setDate(today.getDate() - interval);
        }

        return new Date(v) <= pastDate ||
            msg || t('validation.date_must_be_past', { interval, unit });
    },

    // Custom format validation rules
    zipCodeRule: (msg) => v => !v || /^\d{5}(-\d{4})?$/.test(v) || msg || t('validation.invalid_zip_code'),
    ipAddressRule: (msg) => v => !v || /^(\d{1,3}\.){3}\d{1,3}$/.test(v) || msg || t('validation.invalid_ip_address'),

    // File validation rules
    fileTypeRule: (types, msg) => v => {
        if (!v || !v.type) return true;
        const allowedTypes = Array.isArray(types) ? types : [types];
        return allowedTypes.includes(v.type) || msg || t('validation.file_type_must_be', { types: allowedTypes.join(', ') });
    },
    fileSizeRule: (maxSize, msg) => v => {
        if (!v || !v.size) return true;
        return v.size <= maxSize || msg || t('validation.file_size_must_not_exceed', { size: (maxSize/1024/1024).toFixed(2) });
    },
    // Comparison validation rules
    equalsRule: (target, msg) => v => v === target || msg || t('validation.must_equal', { target }),
    notEqualsRule: (target, msg) => v => v !== target || msg || t('validation.must_not_equal', { target }),
    matchesRule: (field, msg) => (v, formData) => v === formData[field] || msg || t('validation.must_match', { field }),

    // Range validation rules
    betweenRule: (min, max, msg) => v => {
        if (!v) return true;
        const num = Number(v);
        return (num >= min && num <= max) || msg || t('validation.must_be_between', { min, max });
    },
    notBetweenRule: (min, max, msg) => v => {
        if (!v) return true;
        const num = Number(v);
        return (num < min || num > max) || msg || t('validation.must_not_be_between', { min, max });
    },

    // String content validation rules
    containsRule: (substring, msg) => v => !v || v.includes(substring) || msg || t('validation.must_contain', { substring }),
    notContainsRule: (substring, msg) => v => !v || !v.includes(substring) || msg || t('validation.must_not_contain', { substring }),
    startsWithRule: (prefix, msg) => v => !v || v.startsWith(prefix) || msg || t('validation.must_start_with', { prefix }),
    endsWithRule: (suffix, msg) => v => !v || v.endsWith(suffix) || msg || t('validation.must_end_with', { suffix }),

    // Pattern validation rules
    patternRule: (pattern, msg) => v => !v || pattern.test(v) || msg || t('validation.invalid_format'),
    customFormatRule: (formats, msg) => v => {
        if (!v) return true;
        const patterns = {
            creditCard: /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13})$/,
            ssn: /^(?!000|666)[0-8][0-9]{2}-(?!00)[0-9]{2}-(?!0000)[0-9]{4}$/,
            hex: /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i,
            rgb: /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            isbn: /^(?:ISBN(?:-1[03])?:? )?(?=[0-9X]{10}$|(?=(?:[0-9]+[- ]){3})[- 0-9X]{13}$|97[89][0-9]{10}$|(?=(?:[0-9]+[- ]){4})[- 0-9]{17}$)(?:97[89][- ]?)?[0-9]{1,5}[- ]?[0-9]+[- ]?[0-9]+[- ]?[0-9X]$/,
        };
        const formatArray = Array.isArray(formats) ? formats : [formats];
        return formatArray.every(format => patterns[format].test(v)) || msg || t('validation.invalid_custom_format', { formats: formatArray.join(' and ') });
    },

    // Array/Collection validation rules
    uniqueRule: (msg) => v => {
        if (!Array.isArray(v)) return true;
        return v.length === new Set(v).size || msg || t('validation.must_contain_unique_values');
    },
    includesAnyRule: (values, msg) => v => {
        if (!Array.isArray(v)) return true;
        return values.some(val => v.includes(val)) || msg || t('validation.must_include_at_least_one_of', { values: values.join(', ') });
    },
    includesAllRule: (values, msg) => v => {
        if (!Array.isArray(v)) return true;
        return values.every(val => v.includes(val)) || msg || t('validation.must_include_all_of', { values: values.join(', ') });
    },

    // Conditional validation rules
    whenRule: (condition, thenRules, elseRules = []) => (v, formData) => {
        const rules = (typeof condition === 'function' ? condition(formData) : condition) ? thenRules : elseRules;
        return rules.every(rule => rule(v, formData) === true) || t('validation.conditional_validation_failed');
    },
    dependentRule: (field, rules, msg) => (v, formData) => {
        if (!formData[field]) return true;
        return rules.every(rule => rule(v, formData) === true) || msg || t('validation.invalid_based_on', { field });
    },




    generateInputRules,
    validateInput,
  })

  // function invokeRuleValidator () {
  //   const camelSlotName = _.camelCase(slotName)

  //   if (obj.schema.hasOwnProperty('slotHandlers') &&
  //     obj.schema.slotHandlers.hasOwnProperty(camelSlotName)) {
  //     const name = _.camelCase(obj.schema.slotHandlers[camelSlotName])
  //     const func = `${name}Handler`
  //     return methods[func](obj, camelSlotName)
  //   }
  // }

  function generateInputRules(input){
    let availableRules = []

    if (Object.prototype.hasOwnProperty.call(input, 'rules')) {
      let rules = input.rawRules ?? input.rules

      let arrayRules
      if (isString(rules)) {
        arrayRules = rules.split('|')
      } else {
        arrayRules = rules
      }

      arrayRules.forEach((rule, index) => {
        if (isString(rule)) {
          rule = rule.split(':')
        }
        const method = rule[0] + 'Rule'
        if (Object.prototype.hasOwnProperty.call(ruleMethods, method)) {
          availableRules.push(ruleMethods[method](...(rule.slice(1))))
        }
      })
    }

    return availableRules
  }

  function validateInput(input, v){
    let ruleFuncs = generateInputRules(input)

    let isValid = true

    for(const i in ruleFuncs){
      let result = ruleFuncs[i](v)
      if(result !== true){
        isValid = result
        break
      }
    }

    return isValid
  }

  function invokeRule(input){
    let Input = cloneDeep(input)
    let _input = cloneDeep(input)

    if (Object.prototype.hasOwnProperty.call(_input, 'rules')) {
      Input.rawRules = !isset(Input.rawRules)
        ? _input.rules
        : Input.rawRules

      if (isString(Input.rawRules)) {
        _input.rules = Input.rawRules.split('|')
      } else {
        _input.rules = Input.rawRules
      }
      Input.rules = []
      _input.rules.forEach((rule, index) => {
        if (isString(rule)) {
          rule = rule.split(':')
        }
        const method = rule[0] + 'Rule'
        if (Object.prototype.hasOwnProperty.call(ruleMethods, method)) {
          Input.rules.push(ruleMethods[method](...(rule.slice(1))))
        }
      })
    }

    if(isset(Input.schema)){
      Input.schema = invokeRuleGenerator(Input.schema)
    }

    if(!isset(input.rawRules)){
      Input.rules = []
      Input.rawRules = ''
    }

    return Input
  }

  function invokeRuleGenerator (inputs) {
    const _inputs = cloneDeep(inputs)

    if (isObject(_inputs)) {
      Object.keys(_inputs).forEach((name) => {
        if (Object.prototype.hasOwnProperty.call(_inputs[name], 'rules')) {
          inputs[name].rawRules = !isset(inputs[name].rawRules)
            ? _inputs[name].rules
            : inputs[name].rawRules

          if (isString(inputs[name].rawRules)) {
            _inputs[name].rules = inputs[name].rawRules.split('|')
          } else {
            _inputs[name].rules = inputs[name].rawRules
          }
          inputs[name].rules = []
          _inputs[name].rules.forEach((rule, index) => {
            if (isString(rule)) {
              rule = rule.split(':')
            }
            const method = rule[0] + 'Rule'
            if (Object.prototype.hasOwnProperty.call(ruleMethods, method)) {
              inputs[name].rules.push(ruleMethods[method](...(rule.slice(1))))
              // try {
              //
              // } catch (error) {
              //   delete inputs[name].rules[index]
              // }
            }
          })
        }
        if(isset(inputs[name].schema)){
          inputs[name].schema = invokeRuleGenerator(inputs[name].schema)
        }
      })
    }

    return inputs
  }

  watch(() => state.valid, (newValue, oldValue) => {

  })
  // expose managed state as return value
  return {
    // invokeRuleValidator,
    invokeRule,
    invokeRuleGenerator,
    ...toRefs(ruleMethods),
    ...toRefs(state)
  }
}
